import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData, getCodeData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import { menuInfo } from "@shared/type/CommonType";
import SearchPanel from "@widgets/SearchPanel";
import { showDialog } from "@widgets/CommonDialog";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnForm, { ValueChangedEvent } from "@widgets/SnForm";
import { Button } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

interface Props {
  Menuprops: menuInfo;
}

// 승인방식(SYS055) 자동승인 코드값
const AUTO_APPROVE = "2";

// 통제항목 값 컬럼: tp_ctrl(H/D/C/E) → mas_sotype_rule 저장 컬럼
//  C(YN)→yn_value, E(에딧,숫자)→qt_value, D/H(코드)→cd_value
const ruleValueField = (tp: string): "yn_value" | "cd_value" | "qt_value" =>
  tp === "C" ? "yn_value" : tp === "E" ? "qt_value" : "cd_value";

// 섹션 앵커 내비 버튼 — SnNavBtn 과 동일 디자인이되 textTransform:none
// (SnNavBtn 은 MUI 기본 대문자 변환이라 "Workflow"가 "WORKFLOW"로 커 보임)
const NavBtn: React.FC<{ text: string; onClick: () => void }> = ({
  text,
  onClick,
}) => (
  <Button
    variant="outlined"
    size="small"
    onClick={onClick}
    startIcon={<ArrowDropDownIcon />}
    sx={{
      backgroundColor: "rgb(167,175,183)",
      border: "1px solid rgb(186,191,198)",
      borderRadius: "0px",
      padding: "3px 3px",
      color: "white",
      fontWeight: "bold",
      textTransform: "none",
      minWidth: "90px",
      height: "35px",
      "&:hover": { backgroundColor: "rgb(11,12,105)", color: "white" },
      "& .MuiButton-startIcon": { marginRight: "0px" },
    }}
  >
    {text}
  </Button>
);

// 수주형태등록
const SN_SAL_BAS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
    freeform2: React.createRef(),
  });
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [formControls, setFormControls] = useState<any>({
    freeform1: [],
    freeform2: [],
  });
  const [formDatas, setFormDatas] = useState<any>({
    freeform1: null,
    freeform2: null,
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  // _01 다중결과셋으로 한 번에 받아 캐시(행 선택 시 cd_so 로 필터). rs1/2/3
  const allWf = useRef<any[]>([]);
  const allRule = useRef<any[]>([]);
  const allNs = useRef<any[]>([]);
  // 통제항목: 현재 선택 수주형태의 원본 값(피벗) + 메타(cd_ctrl→tp_ctrl/yn_exists). 저장 시 언피벗.
  const ruleOrig = useRef<{ [cd_ctrl: string]: any }>({});
  const ruleMeta = useRef<{ [cd_ctrl: string]: { tp: string; exists: string } }>({});
  // 기본정보 Workflow 표시용: fg_process(=fg_trns) → no_wf 매핑
  const wfByProcess = useRef<{ [k: string]: string }>({});

  // 섹션 앵커 (상단 내비 클릭 시 해당 섹션으로 스크롤 이동)
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLDivElement>(null);
  const wfRef = useRef<HTMLDivElement>(null);
  const ruleRef = useRef<HTMLDivElement>(null);
  const nsRef = useRef<HTMLDivElement>(null);
  // 바깥(전체) 스크롤이 아니라 컨테이너 내부만 스크롤한다.
  const scrollTo = (r: React.RefObject<HTMLDivElement>) => {
    const box = scrollBoxRef.current;
    const el = r.current;
    if (!box || !el) return;
    box.scrollTo({
      top:
        box.scrollTop +
        el.getBoundingClientRect().top -
        box.getBoundingClientRect().top,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({ ...(rtn.formData || {}), ...(prev || {}) }));
        // yn_auto·yn_attach 체크박스는 네이티브 편집불가로 잠근다(onCellClicked 가 토글/규칙 처리) →
        // 에디터 커밋 타이밍 문제 제거 + 자동처리여부=Y 일 때 첨부필수를 사용자가 못 바꾸게 함.
        const grid2Cols = (rtn.grid.grid2 || []).map((c: any) =>
          c.cd_column === "yn_auto" || c.cd_column === "yn_attach"
            ? { ...c, dc_editcon: "true" }
            : c
        );
        // 후속업무 yn_use 체크박스도 편집불가로 잠근다(onCellClicked 가 토글/규칙 처리).
        const grid3Cols = (rtn.grid.grid3 || []).map((c: any) =>
          c.cd_column === "yn_use" ? { ...c, dc_editcon: "true" } : c
        );
        setColums({
          grid1: rtn.grid.grid1,
          grid2: grid2Cols,
          grid3: grid3Cols,
        });
        setFormControls({
          freeform1: rtn.form.freeform1,
          freeform2: rtn.form.freeform2 || [],
        });
        setFormDatas({ freeform1: null, freeform2: null });

        // 기본정보 Workflow 표시용 매핑 (fg_process → no_wf)
        const wfh = await getCodeData(
          "",
          `select fg_process as code, no_wf as name from mas_workflowh ` +
            `where cd_firm = '${userInfo.cd_firm}' and cd_module = 'SAL'`
        );
        const map: { [k: string]: string } = {};
        (wfh || []).forEach((r: any) => (map[r.code] = r.name));
        wfByProcess.current = map;
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({ onSearch, onInsert, onDelete, onSave }));

  // ── 자식 3섹션 초기화 ──────────────────────────────────────────
  const clearChildren = () => {
    setDataSources((prev: any) => ({ ...prev, grid2: [], grid3: [] }));
    setFormDatas((prev: any) => ({ ...prev, freeform2: null }));
    ruleOrig.current = {};
    ruleMeta.current = {};
    allWf.current = [];
    allRule.current = [];
    allNs.current = [];
  };

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    if (modifiedRowsH.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const params = {
        id: "sn_sal_bas_reg_003_01",
        params: searchRef.current.getFormdata(),
      };

      const data: any = await fnGetDataSet(params);
      setFormDatas({ freeform1: null });
      clearChildren();
      // _01 다중결과셋: [0]목록 [1]Workflow [2]통제항목 [3]후속업무 (전 수주형태분)
      allWf.current = data[1] || [];
      allRule.current = data[2] || [];
      allNs.current = data[3] || [];
      setDataSources((prev: any) => ({ ...prev, grid1: data[0] || [] }));

      gridRefs.current["grid1"].current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = () => {
    gridRefs.current["grid1"].current?.addRow();
  };

  const onBeforeAddRow = (): any => {
    try {
      const searchFormData = searchRef.current.getFormdata();
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: searchFormData.cd_biz,
        yn_use: "Y",
        yn_app: "N",
        yn_autoreq: "N",
        yn_autoisu: "N",
        yn_autosales: "N",
        yn_trade: "N",
        yn_grw: "Y",
        fg_um: "",
        tp_um: "",
        tp_sales: "",
        tp_um_vat: "1",
        cd_exch: "000",
        fg_trns: "1",
        fg_money: "1",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid1"].current?.deleteSelectedRows();

    if (gridRefs.current["grid1"].current.getDisplayedRowCount() < 1) {
      setFormDatas({ freeform1: null });
      clearChildren();
    }
  };

  // 자식 그리드 변경분을 insert/update 로 재분류(카탈로그 LEFT JOIN 이라 grid 는 전부 update 로 봄).
  //  yn_exists='Y' → update, 아니면 insert. (PK: cd_firm, cd_so, +키)
  const splitChildBucket = (gridKey: string) => {
    const ch = gridRefs.current[gridKey].current?.getChange() ?? {};
    const upd = ch.update || [];
    return {
      insert: [...(ch.insert || []), ...upd.filter((r: any) => r.yn_exists !== "Y")],
      update: upd.filter((r: any) => r.yn_exists === "Y"),
      delete: ch.delete || [],
    };
  };

  // 통제항목(freeform2) 변경분을 mas_sotype_rule 행으로 언피벗.
  //  폼값(cd_param=cd_ctrl) vs 원본 비교 → 변경분만, tp_ctrl 로 저장 컬럼 결정, yn_exists 로 insert/update.
  const buildRuleBucket = () => {
    const insert: any[] = [];
    const update: any[] = [];
    const cd_so = ruleOrig.current.cd_so;
    if (!cd_so) return { insert, update, delete: [] };

    const cur = formRefs.current["freeform2"]?.current?.getFormdata?.() ?? {};
    Object.keys(ruleMeta.current).forEach((cd_ctrl) => {
      const { tp, exists } = ruleMeta.current[cd_ctrl];
      const newVal = cur[cd_ctrl];
      const oldVal = ruleOrig.current[cd_ctrl];
      if (String(newVal ?? "") === String(oldVal ?? "")) return; // 미변경 skip

      const field = ruleValueField(tp);
      const rowObj: any = {
        cd_firm: userInfo.cd_firm,
        cd_so,
        cd_ctrl,
        yn_value: "N",
        cd_value: null,
        qt_value: 0,
      };
      rowObj[field] =
        field === "qt_value"
          ? Number(newVal) || 0
          : field === "yn_value"
            ? newVal ?? "N"
            : (newVal ?? null);
      (exists === "Y" ? update : insert).push(rowObj);
    });
    return { insert, update, delete: [] };
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();
      gridRefs.current["grid3"].current?.stopEditing();

      const requiredValuesCheck =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheck) return false;

      const gridGetChanges = gridRefs.current["grid1"].current.getChange();
      const wf = splitChildBucket("grid2");
      const ns = splitChildBucket("grid3");
      const rule = buildRuleBucket();

      // 승인방식(tp_approve)은 NOT NULL. 미선택(빈값)이면 기본 수동승인('1')으로 보정.
      [...wf.insert, ...wf.update].forEach((r: any) => {
        if (!r.tp_approve) r.tp_approve = "1";
      });

      const childCount =
        wf.insert.length + wf.update.length + wf.delete.length +
        ns.insert.length + ns.update.length + ns.delete.length +
        rule.insert.length + rule.update.length;

      if (gridGetChanges.modifiedRowcount == 0 && childCount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const overCd = [
        ...(gridGetChanges.insert || []),
        ...(gridGetChanges.update || []),
      ].some((item) => item.cd_so && item.cd_so.length > 6);
      if (overCd) {
        await showDialog("alter", "수주형태코드가 6자리를 초과하였습니다.");
        return;
      }
      const overNm = [
        ...(gridGetChanges.insert || []),
        ...(gridGetChanges.update || []),
      ].some((item) => item.nm_so && item.nm_so.length > 100);
      if (overNm) {
        await showDialog("alter", "수주형태명이 100자리를 초과하였습니다.");
        return;
      }

      const saveData = {
        mas_sotype: gridGetChanges,
        mas_sotype_workflow: wf,
        mas_sotype_rule: rule,
        mas_sotype_nextstep: ns,
      };

      const ok = await fnSave("SN_SAL_BAS_REG_003_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid3"].current?.resetUpdate();
      // 저장 후 통제항목 원본/존재플래그 갱신(재조회 없이). 변경된 항목은 이제 행이 존재.
      const cur = formRefs.current["freeform2"]?.current?.getFormdata?.() ?? {};
      Object.keys(ruleMeta.current).forEach((k) => {
        if (String(cur[k] ?? "") !== String(ruleOrig.current[k] ?? "")) {
          ruleMeta.current[k] = { ...ruleMeta.current[k], exists: "Y" };
        }
      });
      ruleOrig.current = { ...ruleOrig.current, ...cur };
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  // 수주형태 행 선택 → 상세폼 + 자식 3섹션 로드
  const onFocusedRowChanged = async (event: any) => {
    const row = event;
    if (!row) return;
    // Workflow(표시용): fg_trns 에 맞는 no_wf 를 폼 표시값으로만 주입(저장에는 안 실림)
    setFormDatas({
      freeform1: { ...row, no_wf: wfByProcess.current[row.fg_trns] ?? "" },
    });

    if (!row.cd_so) {
      clearChildren();
      return;
    }

    try {
      // _01 로 이미 받아둔 전체 자식데이터에서 선택 cd_so 만 필터(추가 조회 없음).
      // {...r} 얕은 복사 — 그리드 편집이 캐시(allWf/allNs) 원본을 오염시키지 않게.
      const cdSo = row.cd_so;
      const pick = (cache: any[]) =>
        cache.filter((r) => r.cd_so === cdSo).map((r) => ({ ...r }));

      setDataSources((prev: any) => ({
        ...prev,
        grid2: pick(allWf.current),
        grid3: pick(allNs.current),
      }));
      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid3"].current?.resetUpdate();

      // 통제항목(freeform2): mas_sotype_rule 행들을 cd_ctrl 키로 피벗해 폼 데이터로 만든다.
      //  sys_formctrl 의 통제항목 컨트롤은 cd_param = 각 cd_ctrl 로 정의한다.
      const rules = allRule.current.filter((r) => r.cd_so === cdSo);
      const pivot: { [k: string]: any } = { cd_firm: userInfo.cd_firm, cd_so: cdSo };
      const meta: { [k: string]: { tp: string; exists: string } } = {};
      rules.forEach((r) => {
        const field = ruleValueField(r.tp_ctrl);
        pivot[r.cd_ctrl] = r[field];
        meta[r.cd_ctrl] = { tp: r.tp_ctrl, exists: r.yn_exists };
      });
      ruleOrig.current = { ...pivot };
      ruleMeta.current = meta;
      setFormDatas((prev: any) => ({ ...prev, freeform2: pivot }));

      // 그리드(suppressInitialCellFocus)·폼(autoFocus:false)이 로드 시 포커스를 안 가져가므로
      // 아래 섹션으로 스크롤이 튀지 않는다. 편집 시작점만 기본정보 상단(수주형태명)으로 지정.
      // (이후 Tab 은 SnForm 이 컨트롤 순서대로 이동)
      setTimeout(() => {
        formRefs.current["freeform1"]?.current?.setFocus?.("nm_so");
      }, 60);
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onValueChanged = async (event: ValueChangedEvent) => {
    const param = event.colId;
    const data = event.newValue;
    const formData = event.rowData;

    switch (param) {
      case "fg_trns":
        return { yn_trade: data === "1" ? "N" : "Y" };
      case "yn_autoreq":
        if (data === "Y" && formData.yn_app === "N") return { yn_app: "Y" };
        return {};
      case "yn_autoisu":
        if (data === "Y") {
          let rtnData: any = {};
          if (formData.yn_app === "N") rtnData["yn_app"] = "Y";
          if (formData.yn_autoreq === "N") rtnData["yn_autoreq"] = "Y";
          return rtnData;
        }
        return {};
      case "yn_autosales":
        if (data === "Y") {
          let rtnData: any = {};
          if (formData.yn_app === "N") rtnData["yn_app"] = "Y";
          if (formData.yn_autoreq === "N") rtnData["yn_autoreq"] = "Y";
          if (formData.yn_autoisu === "N") rtnData["yn_autoisu"] = "Y";
          return rtnData;
        }
        return {};
      default:
        return {};
    }
  };

  // 자동처리여부/첨부필수 체크박스는 편집불가로 잠그고 토글을 여기서 동기 처리한다.
  //  - yn_auto 클릭: 토글. Y가 되면 승인방식=자동승인, 첨부필수=uncheck.
  //  - yn_attach 클릭: 자동처리여부=Y면 무시(잠금 유지), 아니면 토글.
  const onWorkflowCellClicked = (p: any) => {
    if (!p) return;
    const node = p.node;
    if (!node) return;
    const row = node.data || {};

    if (p.colId === "yn_auto") {
      if (Number(row.no_seq) === 1) return; // 1행 자동처리여부 항상 N (토글 불가)
      const next = row.yn_auto === "Y" ? "N" : "Y";
      // Y로 갈 땐 의존 셀(승인방식·첨부)을 먼저 세팅한다.
      // (yn_auto=Y를 먼저 세팅하면 그 셀들이 잠겨 setDataValue 가 막힌다)
      if (next === "Y") {
        node.setDataValue("tp_approve", AUTO_APPROVE);
        node.setDataValue("yn_attach", "N");
      }
      node.setDataValue("yn_auto", next);
      return;
    }

    if (p.colId === "yn_attach") {
      if (row.yn_auto === "Y") return; // 자동처리여부=Y 면 첨부필수 변경 불가
      node.setDataValue("yn_attach", row.yn_attach === "Y" ? "N" : "Y");
    }
  };

  // 후속업무 yn_use 체크박스도 편집불가로 잠그고 여기서 토글한다.
  //  사용=N 이면 수량기준/실행시점을 빈값으로. 콤보(SnComboBox)는 빈값 setDataValue 가 막히므로
  //  행 전체를 sharedCellValueChanged(applyTransaction)로 갱신한다(값 반영 + 변경추적).
  const onNextstepCellClicked = (p: any) => {
    if (!p || p.colId !== "yn_use") return;
    const node = p.node;
    if (!node) return;
    const row = node.data || {};
    const next = row.yn_use === "Y" ? "N" : "Y";
    const patch: any = { ...row, yn_use: next };
    if (next === "N") {
      patch.fg_qty = "";
      patch.fg_execute = "";
    }
    gridRefs.current["grid3"].current?.sharedCellValueChanged?.(patch);
  };

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        events: { onValueChanged },
      },
      freeform2: {
        controls: formControls.freeform2,
        data: formDatas.freeform2,
      },
    }),
    [formControls, formDatas]
  );

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100vh - 110px)", overflow: "hidden" }}
    >
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex flex-1 min-h-0 space-x-1.5">
        {/* 좌측: 수주형태 목록 */}
        <SnResizeBar direction={"width"} id="panel1" orgSize="40%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={columns.grid1}
            rowData={dataSources.grid1}
            etcProps={{ rowSelection: "single", title: "수주형태" }}
            events={{ onFocusedRowChanged, onBeforeAddRow }}
          />
        </SnResizeBar>

        {/* 우측: 상단 섹션 내비 + 고정 높이 스크롤 컨테이너 (품목등록 스타일) */}
        <SnResizeBar direction={"width"} id="panel2" orgSize="60%">
          <div className="h-full flex flex-col min-h-0">
            {/* 섹션 내비 — 클릭 시 해당 섹션으로 스크롤 이동 */}
            <div className="flex gap-1 shrink-0">
              {[
                { label: "기본정보", ref: baseRef },
                { label: "Workflow", ref: wfRef },
                { label: "통제항목", ref: ruleRef },
                { label: "후속업무", ref: nsRef },
              ].map((t) => (
                <NavBtn
                  key={t.label}
                  text={t.label}
                  onClick={() => scrollTo(t.ref)}
                />
              ))}
            </div>

            {/* 남은 높이 안에서만 세로 스크롤 (최상위가 하드 바운드됨) */}
            <div
              ref={scrollBoxRef}
              className="flex-1 min-h-0 overflow-y-auto border pr-1"
            >
              {/* 1. 기본정보 — 전 항목이 스크롤 없이 보이도록 충분한 높이 */}
              <div ref={baseRef} style={{ height: 360 }}>
                <SnForm
                  ref={formRefs.current["freeform1"]}
                  gridref={gridRefs.current["grid1"]}
                  data={forms.freeform1.data}
                  controls={forms.freeform1.controls}
                  events={forms.freeform1.events}
                  etcProps={{ title: "기본정보", autoFocus: false }}
                />
              </div>

              {/* 2. Workflow */}
              <div ref={wfRef} style={{ height: 280 }} className="mt-2 wf-checkbox-passthrough">
                {/* 잠근 체크박스(yn_auto/yn_attach)가 클릭을 삼켜 셀 클릭이 안 뜨는 것 방지 —
                    체크박스에 pointer-events:none 을 줘 클릭이 셀로 통과되게 한다. */}
                <style>{`.wf-checkbox-passthrough input[type="checkbox"]{pointer-events:none;}`}</style>
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={columns.grid2}
                  rowData={dataSources.grid2}
                  etcProps={{
                    isEditable: true,
                    rowSelection: "single",
                    title: "Workflow",
                    suppressInitialCellFocus: true,
                  }}
                  events={{ onCellClicked: onWorkflowCellClicked }}
                />
              </div>

              {/* 3. 통제항목 — sys_formctrl(freeform2) 기반 SnForm */}
              <div ref={ruleRef} style={{ height: 360 }} className="mt-2">
                <SnForm
                  ref={formRefs.current["freeform2"]}
                  gridref={gridRefs.current["grid1"]}
                  data={forms.freeform2.data}
                  controls={forms.freeform2.controls}
                  etcProps={{ title: "통제항목", autoFocus: false }}
                />
              </div>

              {/* 4. 후속업무 */}
              <div
                ref={nsRef}
                style={{ height: 240 }}
                className="mt-2 wf-checkbox-passthrough"
              >
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={columns.grid3}
                  rowData={dataSources.grid3}
                  etcProps={{
                    isEditable: true,
                    rowSelection: "single",
                    title: "후속업무",
                    suppressInitialCellFocus: true,
                  }}
                  events={{ onCellClicked: onNextstepCellClicked }}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SAL_BAS_REG_003;
